// Persists the most-recent failed bulk-stage upload to localStorage so the
// admin can navigate away and come back without losing the error list.
// Last-upload-wins: each new terminal phase with errors overwrites the prior
// entry. Successful uploads (no errors) are NOT persisted — there's nothing
// actionable to come back to.

const STORAGE_KEY = "nysmahBulkStageLastResult";
// Bumped to 2 when the dispatcher rolled out and `succeeded`/`failed` were
// replaced with `live`/`staged`/`rejected`. Old v1 records are discarded
// rather than reshaped — they're transient failure logs, not user data.
const STORAGE_VERSION = 2;
// Conservative cap; localStorage typically caps at 5MB but other apps share it.
// 500-row uploads serialize to well under this in practice.
const MAX_BYTES = 1_500_000;

export function saveLastResult(payload) {
  try {
    const wrapped = {
      version: STORAGE_VERSION,
      savedAt: new Date().toISOString(),
      phase: payload.phase,
      fileName: payload.fileName || null,
      fileSize: payload.fileSize || null,
      total: payload.total || 0,
      processed: payload.processed || 0,
      live: payload.live || 0,
      staged: payload.staged || 0,
      rejected: payload.rejected || 0,
      summary: payload.summary || null,
      rowFailures: payload.rowFailures || [],
      parsedRows: payload.parsedRows || null,
      incompleteReason: payload.incompleteReason || null,
    };

    let json = JSON.stringify(wrapped);
    if (json.length > MAX_BYTES) {
      // Drop parsedRows so the failure list itself still survives (the
      // "Download failed rows" feature needs parsedRows; gracefully degrade
      // to view-only when over the cap).
      const trimmed = { ...wrapped, parsedRows: null, _trimmed: true };
      json = JSON.stringify(trimmed);
      if (json.length > MAX_BYTES) {
        console.warn("[bulkStagePersistence] result too large to persist");
        return false;
      }
    }
    localStorage.setItem(STORAGE_KEY, json);
    return true;
  } catch (e) {
    // localStorage can throw QuotaExceededError or be disabled (private mode).
    // Failing to persist is non-fatal — the user just loses the resume
    // affordance for that upload.
    console.warn("[bulkStagePersistence] save failed", e);
    return false;
  }
}

export function loadLastResult() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data?.version !== STORAGE_VERSION) {
      // Schema change — discard old data rather than risk shape mismatches.
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch (e) {
    console.warn("[bulkStagePersistence] load failed", e);
    return null;
  }
}

export function clearLastResult() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
}
